import {
  deleteProfile as deleteCachedProfile,
  listProfiles as listCachedProfiles,
  replaceProfiles,
  saveProfile as saveCachedProfile,
} from './storage.js';

const API_PATH = '/api/shared-profiles';
const REQUEST_TIMEOUT_MS = 8000;

function browserIsOffline() {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

function announce(state, message) {
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('reelpro:profile-sync', { detail: { state, message } }));
}

async function api(path = '', options = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(`${API_PATH}${path}`, {
      ...options,
      headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
      signal: controller.signal,
    });
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.toLowerCase().includes('application/json')) throw new Error('Shared profile service is unavailable.');
    const payload = await response.json();
    if (!response.ok) {
      const error = new Error(payload.error || 'Profile sync failed.');
      error.status = response.status;
      error.current = payload.current || null;
      throw error;
    }
    return payload;
  } finally {
    clearTimeout(timer);
  }
}

function visible(profiles) {
  return profiles.filter(profile => !profile.deleted);
}

async function putRemote(profile) {
  const payload = await api(`/${encodeURIComponent(profile.id)}`, {
    method: 'PUT',
    body: JSON.stringify({ name: profile.name, settings: profile.settings, version: Number(profile.version || 0) }),
  });
  return { ...payload.profile, syncState: 'synced', deleted: false };
}

async function deleteRemote(profile) {
  if (!Number(profile.version || 0)) return;
  await api(`/${encodeURIComponent(profile.id)}?version=${Number(profile.version)}`, { method: 'DELETE' });
}

async function flushPending(cached) {
  for (const profile of cached.filter(item => item.syncState === 'pending' || !item.syncState)) {
    const legacyProfile = !profile.syncState;
    try {
      if (profile.deleted) {
        await deleteRemote(profile);
        await deleteCachedProfile(profile.id);
      } else {
        const synced = await putRemote(profile);
        await saveCachedProfile(synced);
      }
    } catch (error) {
      if (legacyProfile && error.status === 409 && error.current) {
        await saveCachedProfile({ ...error.current, syncState: 'synced', deleted: false });
        continue;
      }
      throw error;
    }
  }
}

export async function listProfiles() {
  const cached = await listCachedProfiles();
  if (browserIsOffline()) {
    announce('offline', 'Offline · cached profiles');
    return visible(cached);
  }
  announce('syncing', 'Syncing profiles…');
  try {
    await flushPending(cached);
    const payload = await api();
    const remote = (payload.profiles || []).map(profile => ({ ...profile, syncState: 'synced', deleted: false }));
    await replaceProfiles(remote);
    announce('synced', 'Profiles synced');
    return remote;
  } catch (error) {
    if (error.status === 409) announce('conflict', error.message);
    else announce('offline', 'Offline · cached profiles');
    return visible(cached);
  }
}

export async function saveProfile(profile) {
  const draft = { ...profile, version: Number(profile.version || 0), syncState: 'pending', deleted: false, updatedAt: new Date().toISOString() };
  await saveCachedProfile(draft);
  if (browserIsOffline()) {
    announce('offline', 'Saved offline · sync pending');
    return draft;
  }
  announce('syncing', 'Publishing profile…');
  try {
    const synced = await putRemote(draft);
    await saveCachedProfile(synced);
    announce('synced', 'Profiles synced');
    return synced;
  } catch (error) {
    if (error.status === 409) {
      if (error.current) await saveCachedProfile({ ...error.current, syncState: 'synced', deleted: false });
      announce('conflict', error.message);
      throw error;
    }
    announce('offline', 'Saved offline · sync pending');
    return draft;
  }
}

export async function deleteProfile(id) {
  const cached = await listCachedProfiles();
  const current = cached.find(profile => profile.id === id);
  if (!current) return { pending: false };
  if (!Number(current.version || 0)) {
    await deleteCachedProfile(id);
    return { pending: false };
  }
  const tombstone = { ...current, deleted: true, syncState: 'pending', updatedAt: new Date().toISOString() };
  await saveCachedProfile(tombstone);
  if (browserIsOffline()) {
    announce('offline', 'Deletion queued for sync');
    return { pending: true };
  }
  announce('syncing', 'Removing shared profile…');
  try {
    await deleteRemote(tombstone);
    await deleteCachedProfile(id);
    announce('synced', 'Profiles synced');
    return { pending: false };
  } catch (error) {
    if (error.status === 409) {
      if (error.current) await saveCachedProfile({ ...error.current, syncState: 'synced', deleted: false });
      announce('conflict', error.message);
      throw error;
    }
    announce('offline', 'Deletion queued for sync');
    return { pending: true };
  }
}
